import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { API, useAuth } from '@/App';
import axios from 'axios';
import DashboardLayout from '@/components/DashboardLayout';
import PublicLayout from '@/components/PublicLayout';
import SEO from '@/components/SEO';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { 
  Briefcase, MapPin, Clock, DollarSign, Search, 
  Building2, Filter, Plus, ArrowRight, GraduationCap, BookOpen, Award, Star, CalendarDays,
} from 'lucide-react';

const relativeTime = (iso) => {
  if (!iso) return '';
  try {
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
    if (diff < 86400 * 30) return `${Math.floor(diff / (86400 * 7))}w ago`;
    return `${Math.floor(diff / (86400 * 30))}mo ago`;
  } catch { return ''; }
};

const JobsPage = () => {
  const { user, token } = useAuth();
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [totalJobs, setTotalJobs] = useState(0);
  const [loading, setLoading] = useState(true);
  const [constants, setConstants] = useState(null);
  
  // Filters
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [locationFilter, setLocationFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [subjectFilter, setSubjectFilter] = useState('all');
  const [gradeFilter, setGradeFilter] = useState('all');
  const [boardFilter, setBoardFilter] = useState('all');

  const isRecruiter = ['super_admin', 'client_admin', 'team_leader', 'recruiter'].includes(user?.role);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [jobsRes, constantsRes] = await Promise.all([
          axios.get(`${API}/jobs?limit=500`, { 
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            withCredentials: true 
          }),
          axios.get(`${API}/education/constants`)
        ]);
        setJobs(jobsRes.data.jobs || []);
        setTotalJobs(jobsRes.data.total || 0);
        setConstants(constantsRes.data);
      } catch (error) {
        console.error('Failed to fetch data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [token]);

  const filteredJobs = jobs.filter(job => {
    const q = (search || '').toLowerCase();
    const loc = (locationFilter || '').toLowerCase();
    const title = (job.title || '').toLowerCase();
    const desc = (job.description || '').toLowerCase();
    const jobLoc = (job.location || '').toLowerCase();
    const matchesSearch = !q || title.includes(q) || desc.includes(q);
    const matchesType = typeFilter === 'all' || job.job_type === typeFilter;
    const matchesLocation = !loc || jobLoc.includes(loc);
    const matchesCategory = categoryFilter === 'all' || job.job_category === categoryFilter;
    const matchesSubject = subjectFilter === 'all' || (job.subjects || []).includes(subjectFilter);
    const matchesGrade = gradeFilter === 'all' || (job.grade_levels || []).includes(gradeFilter);
    const matchesBoard = boardFilter === 'all' || (job.board_curriculum || []).includes(boardFilter);
    return matchesSearch && matchesType && matchesLocation && matchesCategory && matchesSubject && matchesGrade && matchesBoard;
  });

  const getJobTypeBadge = (type) => {
    const config = {
      full_time: { label: 'Full Time', className: 'bg-green-100 text-green-800' },
      part_time: { label: 'Part Time', className: 'bg-blue-100 text-blue-800' },
      contract: { label: 'Contract', className: 'bg-orange-100 text-orange-800' },
      internship: { label: 'Internship', className: 'bg-purple-100 text-purple-800' },
    };
    const c = config[type] || config.full_time;
    return <Badge className={c.className}>{c.label}</Badge>;
  };

  const formatSalary = (min, max) => {
    if (!min && !max) return null;
    const format = (n) => `₹${(n/1000).toFixed(0)}k`;
    if (min && max) return `${format(min)} - ${format(max)}/mo`;
    if (min) return `From ${format(min)}/mo`;
    return `Up to ${format(max)}/mo`;
  };

  const content = (
    <div className="space-y-6 animate-fadeIn" data-testid="jobs-page">
      <SEO
        title="Teaching & Education Jobs in India"
        description="Browse teaching jobs, education vacancies, and academic positions across India. Filter by subject, grade level, location, and board. Apply with one click on EdAssist."
        path="/jobs"
      />
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Outfit' }}>
            {isRecruiter ? 'Manage Jobs' : 'Teaching & Education Jobs'}
          </h1>
          <p className="text-slate-600 mt-1">
            {filteredJobs.length} {filteredJobs.length === 1 ? 'position' : 'positions'} available
          </p>
        </div>
        {isRecruiter && (
          <Link to="/jobs/create">
            <Button data-testid="create-job-btn">
              <Plus className="w-4 h-4 mr-2" />
              Post New Job
            </Button>
          </Link>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Search */}
            <div className="lg:col-span-2 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search jobs by title or keyword..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="job-search-input"
              />
            </div>
            
            {/* Location */}
            <div className="relative">
              <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 z-10" />
              <Input
                placeholder="City or State"
                value={locationFilter}
                onChange={(e) => setLocationFilter(e.target.value)}
                className="pl-10"
              />
            </div>

            {/* Job Type */}
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger>
                <Clock className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Job Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="full_time">Full Time</SelectItem>
                <SelectItem value="part_time">Part Time</SelectItem>
                <SelectItem value="contract">Contract</SelectItem>
                <SelectItem value="internship">Internship</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Education-Specific Filters */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t">
            {/* Category */}
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger>
                <GraduationCap className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                <SelectItem value="teaching">Teaching</SelectItem>
                <SelectItem value="non_teaching">Non-Teaching</SelectItem>
              </SelectContent>
            </Select>

            {/* Subject */}
            <Select value={subjectFilter} onValueChange={setSubjectFilter}>
              <SelectTrigger>
                <BookOpen className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Subject" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Subjects</SelectItem>
                {constants?.subjects?.slice(0, 15).map(sub => (
                  <SelectItem key={sub} value={sub}>{sub}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Grade Level */}
            <Select value={gradeFilter} onValueChange={setGradeFilter}>
              <SelectTrigger>
                <SelectValue placeholder="Grade Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Grades</SelectItem>
                {constants?.grade_levels?.filter(l => l && l.toLowerCase() !== 'all grades').map(level => (
                  <SelectItem key={level} value={level}>{level}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Board */}
            <Select value={boardFilter} onValueChange={setBoardFilter}>
              <SelectTrigger>
                <Award className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Board" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Boards</SelectItem>
                {constants?.boards_curriculum?.map(board => (
                  <SelectItem key={board} value={board}>{board}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Job Listings */}
      <div className="space-y-4">
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="text-slate-500 mt-4">Loading jobs...</p>
          </div>
        ) : filteredJobs.length > 0 ? (
          filteredJobs.map((job) => (
            <Link key={job.job_id} to={`/jobs/${job.job_id}`}>
              <Card className="card-hover" data-testid={`job-card-${job.job_id}`}>
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex gap-4">
                      <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                        {job.organization?.logo ? (
                          <img src={job.organization.logo} alt="" className="w-8 h-8 rounded" />
                        ) : (
                          <GraduationCap className="w-6 h-6 text-indigo-600" />
                        )}
                      </div>
                      <div className="flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          {job.is_featured && (
                            <Badge className="bg-amber-100 text-amber-800 border border-amber-200 text-xs" data-testid={`featured-badge-${job.job_id}`}>
                              <Star className="w-3 h-3 mr-1 fill-current" /> Featured
                            </Badge>
                          )}
                          {job.is_sponsored && (
                            <Badge className="bg-indigo-600 text-white text-xs">Sponsored</Badge>
                          )}
                          {getJobTypeBadge(job.job_type)}
                          {job.job_category === 'teaching' && (
                            <Badge variant="outline" className="text-indigo-600 border-indigo-200">
                              <BookOpen className="w-3 h-3 mr-1" />
                              Teaching
                            </Badge>
                          )}
                          {job.demo_lesson_required && (
                            <Badge variant="outline" className="text-orange-600 border-orange-200">Demo Required</Badge>
                          )}
                        </div>
                        <h3 className="font-semibold text-lg text-slate-900" style={{ fontFamily: 'Outfit' }}>
                          {job.title}
                        </h3>
                        <p className="text-slate-600">{job.organization?.name || 'School'}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {job.location}
                          </span>
                          {formatSalary(job.salary_min, job.salary_max) && (
                            <span className="flex items-center gap-1">
                              <DollarSign className="w-4 h-4" />
                              {formatSalary(job.salary_min, job.salary_max)}
                            </span>
                          )}
                          {job.experience_years > 0 && (
                            <span>{job.experience_years}+ years exp</span>
                          )}
                          {job.posted_at && (
                            <span className="flex items-center gap-1" title={new Date(job.posted_at).toLocaleString()}>
                              <CalendarDays className="w-4 h-4" />
                              Posted {relativeTime(job.posted_at)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {isRecruiter && (
                        <Badge variant={job.status === 'published' ? 'default' : 'secondary'}>
                          {job.status}
                        </Badge>
                      )}
                      <ArrowRight className="w-5 h-5 text-slate-400" />
                    </div>
                  </div>
                  
                  {/* Education Tags */}
                  <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-slate-100">
                    {job.subjects?.slice(0, 3).map((subj, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-blue-50">
                        {subj}
                      </Badge>
                    ))}
                    {job.grade_levels?.slice(0, 2).map((grade, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-green-50">
                        {grade}
                      </Badge>
                    ))}
                    {job.board_curriculum?.slice(0, 2).map((board, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-purple-50">
                        {board}
                      </Badge>
                    ))}
                    {job.required_certifications?.slice(0, 2).map((cert, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-orange-50">
                        {cert}
                      </Badge>
                    ))}
                    {(job.accommodation_provided || job.transport_provided) && (
                      <Badge variant="outline" className="text-xs bg-emerald-50 text-emerald-700">
                        {job.accommodation_provided && job.transport_provided 
                          ? 'Accommodation + Transport'
                          : job.accommodation_provided ? 'Accommodation' : 'Transport'}
                      </Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))
        ) : (
          <Card>
            <CardContent className="p-12 text-center">
              <GraduationCap className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-900 mb-2">No jobs found</h3>
              <p className="text-slate-500">
                {search || locationFilter || categoryFilter !== 'all'
                  ? 'Try adjusting your filters'
                  : 'Check back later for new opportunities'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  if (!user) {
    return <PublicLayout>{content}</PublicLayout>;
  }

  return <DashboardLayout>{content}</DashboardLayout>;
};

export default JobsPage;
